import { useCallback, useEffect, useState } from "react";
import { subscriptionRequestService } from "../../services/subscriptionRequestService";
import Icon from "../../components/Common/Icon";
import { useToast } from "../../context/ToastContext";

const fmtTL = (n) =>
  (n == null ? 0 : Number(n)).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

const fmtDate = (s) => (s ? new Date(s).toLocaleString("tr-TR") : "—");

/**
 * ADMIN: onay bekleyen lisans/abonelik talepleri. Ödeme elden alınıp kontrol
 * edildikten sonra "Onayla" ile lisans aktifleşir; "Reddet" ile talep kapatılır.
 * Yönetici lisansını kendisi aktifleştiremez (backend zorlar).
 */
export default function SubscriptionRequestsPage() {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null); // { mode: "approve"|"reject", req }
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    subscriptionRequestService
      .listPending()
      .then((res) => setRows(res.data?.data ?? []))
      .catch((err) => setError(err.response?.data?.error ?? "Talepler yüklenemedi."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const openDecision = (mode, req) => {
    setDecision({ mode, req });
    setNote("");
  };

  const submitDecision = async () => {
    if (!decision) return;
    const { mode, req } = decision;
    setSaving(true);
    try {
      if (mode === "approve") {
        await subscriptionRequestService.approve(req.id, note.trim() || null);
        showToast(`${req.salonName} lisansı aktifleştirildi.`, "success");
      } else {
        await subscriptionRequestService.reject(req.id, note.trim() || null);
        showToast(`${req.salonName} talebi reddedildi.`, "success");
      }
      setDecision(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.error ?? "İşlem başarısız.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Icon name="fact_check" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Lisans Talepleri</h1>
            <p className="text-on-surface-variant text-sm">
              Ödemesi alınan talepleri onaylayın; lisans onayla birlikte aktifleşir.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-sm flex items-center gap-1.5"
        >
          <Icon name="refresh" className="!text-[18px]" /> Yenile
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-container-low text-on-surface-variant text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Salon</th>
                <th className="text-left px-4 py-3 font-semibold">Paket</th>
                <th className="text-right px-4 py-3 font-semibold">Süre</th>
                <th className="text-right px-4 py-3 font-semibold">Tutar</th>
                <th className="text-left px-4 py-3 font-semibold">Talep Eden</th>
                <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold">Not</th>
                <th className="px-4 py-3 text-center font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-on-surface-variant">Yükleniyor…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-on-surface-variant">
                  <Icon name="inbox" className="!text-[32px] block mx-auto mb-2 opacity-50" />
                  Onay bekleyen lisans talebi yok.
                </td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-surface-container-low/50">
                    <td className="px-4 py-3 font-semibold text-on-surface">{r.salonName}</td>
                    <td className="px-4 py-3">{r.packageName}</td>
                    <td className="px-4 py-3 text-right">{r.months} ay</td>
                    <td className="px-4 py-3 text-right font-semibold text-primary">
                      {fmtTL(r.totalPrice)}
                      <div className="text-[11px] font-normal text-on-surface-variant">{fmtTL(r.monthlyPrice)}/ay</div>
                    </td>
                    <td className="px-4 py-3 text-on-surface-variant">{r.createdBy || "—"}</td>
                    <td className="px-4 py-3 text-on-surface-variant">{fmtDate(r.createdAt)}</td>
                    <td className="px-4 py-3 text-on-surface-variant max-w-[16rem] truncate" title={r.note || ""}>{r.note || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          onClick={() => openDecision("approve", r)}
                          className="flex items-center gap-1 bg-success text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:opacity-90 transition"
                        >
                          <Icon name="check" className="!text-[16px]" /> Onayla
                        </button>
                        <button
                          onClick={() => openDecision("reject", r)}
                          className="flex items-center gap-1 border border-error/30 text-error rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-error/10 transition"
                        >
                          <Icon name="close" className="!text-[16px]" /> Reddet
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Karar modalı */}
      {decision && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !saving && setDecision(null)}>
          <div className="bg-surface-card rounded-2xl border border-outline-variant w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Icon
                name={decision.mode === "approve" ? "verified" : "block"}
                className={decision.mode === "approve" ? "text-success" : "text-error"}
              />
              <h3 className="text-lg font-bold text-on-surface">
                {decision.mode === "approve" ? "Talebi Onayla" : "Talebi Reddet"}
              </h3>
            </div>
            <p className="text-sm text-on-surface-variant">
              <strong className="text-on-surface">{decision.req.salonName}</strong> ·{" "}
              {decision.req.packageName} · {decision.req.months} ay · {fmtTL(decision.req.totalPrice)}
            </p>
            <p className="text-sm text-on-surface-variant">
              {decision.mode === "approve"
                ? "Ödemenin alındığını doğruladıysanız onaylayın. Lisans hemen aktifleşecek ve süre uzatılacaktır."
                : "Talep reddedilecek; lisans değişmez. Gerekçe (opsiyonel) yöneticiye iletilir."}
            </p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={decision.mode === "approve" ? "Not (opsiyonel, ör. ödeme referansı)" : "Red gerekçesi (opsiyonel)"}
              className="w-full border border-outline-variant rounded-xl px-3 py-2 text-sm outline-none focus:border-primary resize-none"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDecision(null)}
                disabled={saving}
                className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-sm disabled:opacity-50"
              >
                Vazgeç
              </button>
              <button
                onClick={submitDecision}
                disabled={saving}
                className={`text-white rounded-xl px-5 py-2 font-semibold text-sm hover:opacity-90 transition disabled:opacity-50 ${
                  decision.mode === "approve" ? "bg-success" : "bg-error"
                }`}
              >
                {saving ? "İşleniyor…" : decision.mode === "approve" ? "Onayla ve Aktifleştir" : "Reddet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
